import { SelectableCard } from "@/components/selectable-card/SelectableCard";
import { Button, Typo } from "@pepperstone/pepper-ui-kit";
import clsx from "clsx";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import type {
  CashierPaymentMethod,
  SavedPaymentAccountIdAndFields,
} from "@/api/cashier";
import { useModal } from "@/components/general/base-modal/useModal";
import { UpdateCardExpiryDateModal } from "@/components/modals/UpdateCardExpiryDateModal/UpdateCardExpiryDateModal";

import StatusBadge from "@/components/status-badge/StatusBadge";
import { isCardForm } from "@/utils/helpers";
import styles from "./SavedPaymentMethods.module.css";
import { EIcon } from "@/utils/enums";
import { Icon } from "@/components/general/icon/Icon";

type SavedPaymentMethodProps = {
  selected: SavedPaymentAccountIdAndFields | undefined;
  savedPaymentAccounts: SavedPaymentAccountIdAndFields[] | undefined;
  paymentMethod: CashierPaymentMethod;
  onSelect?: (item: SavedPaymentAccountIdAndFields) => void;
  onAddNewClick?: () => void;
  onDeleteClick?: () => void;
  onExpiryUpdated?: () => void;
  showAddNewButton?: boolean;
};

export const SavedPaymentMethods = ({
  selected,
  paymentMethod,
  savedPaymentAccounts,
  onSelect,
  onAddNewClick,
  onDeleteClick,
  onExpiryUpdated,
  showAddNewButton = true,
}: SavedPaymentMethodProps) => {
  const { t } = useTranslation();
  const [selectedInternal, setSelectedInternal] = useState<string | null>(null);
  const paymentAccounts = useMemo(() => {
    return savedPaymentAccounts?.filter((savedPaymentAccount) => {
      const isCard = isCardForm(paymentMethod);
      if (!isCard) return true;

      // If card form, include only those with a payment_token
      return Boolean(savedPaymentAccount.payment_token);
    });
  }, [savedPaymentAccounts, paymentMethod]);

  useEffect(() => {
    if (selected) {
      const selectedItem = paymentAccounts?.find(
        (item) => item.id === selected.id
      );
      setSelectedInternal(selectedItem?.id ?? null);
    }
  }, [paymentAccounts, selected]);

  const { openModal, modal: CardExpiryModal } = useModal(
    UpdateCardExpiryDateModal
  );

  const handleClick = useCallback(
    (id: string) => {
      setSelectedInternal(id);
      const selectedItem = paymentAccounts?.find((item) => item.id === id);
      if (selectedItem && onSelect) {
        onSelect(selectedItem);
      }
    },
    [paymentAccounts, onSelect]
  );
  return (
    <div
      className={styles.container}
      onClick={(event) => event.stopPropagation()}
    >
      <div className={styles.cardContainer}>
        {paymentAccounts?.map((account) => {
          const id = account.id ?? "";
          const selected = selectedInternal === id;
          const fields = account.saved_payment_account_fields as
            | Record<string, string>
            | undefined;

          return (
            <SelectableCard
              className={clsx(
                styles.savedAccountCard,
                selected && styles.selected
              )}
              classNames={{ tag: styles.tagContainer }}
              key={id}
              id={id}
              data-testid={"saved-account-card"}
              title={account.detail}
              tag={[
                account.is_pending && (
                  <StatusBadge key={`${id}-pending`} type="pending" />
                ),
                fields?.expiry && account.is_expired && (
                  <Button
                    size="ps-btn-sm"
                    type="button"
                    variant="ps-btn-transparent"
                    className="ps-text-text-primary-error ps-text-label-sm-underline"
                    onClick={() =>
                      openModal({
                        savedPaymentAccountID: id,
                        nameOnCard: fields?.nameOnCard ?? fields?.name ?? "",
                        cardNumber: fields?.cardNum ?? "",
                        expiryDate: fields?.expiry ?? "",
                        onSuccess: onExpiryUpdated,
                      })
                    }
                  >
                    {t("deposit.methods.saved_methods.update")}
                  </Button>
                ),
                account.secondary_detail && (
                  <Typo
                    key={`${id}-subtitle`}
                    textColor={
                      account.is_expired
                        ? "ps-text-text-primary-error"
                        : "ps-text-text-base-secondary"
                    }
                    type="body-v2"
                    size="xs"
                    elem="p"
                  >
                    {t("deposit.methods.saved_methods.expiration", {
                      expiryDate: account.secondary_detail,
                    })}
                  </Typo>
                ),
              ].filter(Boolean)}
              button={
                !account.is_pending && (
                  <Button
                    aria-label={t(
                      "deposit.methods.saved_methods.delete_saved_account"
                    )}
                    data-testid={"saved-account-card-delete-button"}
                    variant="ps-btn-background-secondary"
                    size="ps-btn-xs"
                    type="button"
                    onClick={onDeleteClick}
                    icon="delete"
                  />
                )
              }
              selected={selected}
              onClick={handleClick}
            />
          );
        })}
      </div>
      {showAddNewButton && (
        <div>
          <button
            className={styles.addNewPaymentMethodButton}
            type="button"
            onClick={onAddNewClick}
          >
            <Icon
              name={EIcon.Plus}
              className={styles.addNewPaymentMethodIcon}
            />
            {t("deposit.methods.saved_methods.add")}
          </button>
        </div>
      )}
      {CardExpiryModal}
    </div>
  );
};
